import React, { useEffect, useState, useContext } from "react";
import { Box, Card, Text, VStack, Input, Flex } from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import ChatListItem from "../components/ChatListItem";
import ChatLoadingSkeleton from "../loading/ChatLoadingSkeleton";
import axiosInstance from "../routes/axiosInstance";
import { SocketContext } from "../context/SocketContext";
import newMessageAudioFile from "../sounds/new_message_tone.mp3";
import AppFooter from "../components/AppFooter";

const ChatListPage = () => {
  const { currentUser, checkUserAuth } = useAuth();
  const { socket } = useContext(SocketContext);
  const [chats, setChats] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const getChatsData = async () => {
    setLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };

    try {
      const { data } = await axiosInstance.get("/api/chats", config);
      setChats(data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setLoading(false);
      checkUserAuth(error.status);
    }
  };

  const playNewMessageTone = () => {
    const newMessageAudio = new Audio(newMessageAudioFile);
    newMessageAudio.play().catch((error) => {
      console.error("Error playing incoming call sound:", error);
    });
  };

  // to update the chat with new message and unread count
  const handleChatWithNewMessage = (receiverId, unreadCount, messageData) => {
    if (receiverId === currentUser.id) {
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat._id === messageData.chatId
            ? { ...chat, lastMessage: messageData, unreadCount: unreadCount }
            : chat,
        ),
      );
      playNewMessageTone();
    }
  };

  // to update the chat with new message and unread count
  const handleUserTypingInChat = (chatId, Username, userId) => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat._id === chatId ? { ...chat, typing: true } : chat,
      ),
    );
  };

  // to update the chat with new message and unread count
  const handleEndUserTypingInChat = (chatId) => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat._id === chatId ? { ...chat, typing: false } : chat,
      ),
    );
  };

  useEffect(() => {
    if (!socket) return;
    getChatsData();

    // to handle new message event
    socket.on("UPDATE_CHAT_WITH_NEW_MESSAGE", handleChatWithNewMessage);

    // to handle typing message event
    socket.on("USER_TYPING", handleUserTypingInChat);

    // to handle typing message event
    socket.on("USER_TYPING_STOPPED", handleEndUserTypingInChat);

    return () => {
      socket.off("UPDATE_CHAT_WITH_NEW_MESSAGE", handleChatWithNewMessage);
      socket.off("USER_TYPING", handleUserTypingInChat);
      socket.off("USER_TYPING_STOPPED", handleEndUserTypingInChat);
    };
  }, [socket]);

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <VStack spacing={3}>
            <Text
              fontSize="lg"
              fontWeight="600"
              color="gray.800"
              alignSelf="flex-start"
            >
              All Chats
            </Text>
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="gray.50"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="full"
              px={4}
              py={2}
              _focus={{
                borderColor: "teal.400",
                boxShadow: "0 0 0 1px teal.400",
                bg: "white",
              }}
              _hover={{ borderColor: "gray.400" }}
            />
          </VStack>
        </Card>

        {/* Chat List */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          {chats.length ? (
            <VStack align="stretch" spacing={0} w="100%">
              {chats
                .filter((chat) => {
                  if (!searchQuery) return true;

                  const query = searchQuery.toLowerCase();
                  if (chat.isGroup) {
                    // For group chats: match by group name or any member name
                    const matchesChatName = chat.chatName
                      ?.toLowerCase()
                      .includes(query);
                    const matchesMemberName = chat.members?.some((member) =>
                      member.name?.toLowerCase().includes(query),
                    );
                    return matchesChatName || matchesMemberName;
                  } else {
                    // For oto chats: match by the other user's name (exclude self)
                    const otherUser = chat.members?.find(
                      (member) => member._id !== currentUser.id,
                    );
                    return otherUser?.name?.toLowerCase().includes(query);
                  }
                })
                .map((chat, index) => (
                  <Box
                    key={chat._id}
                    borderBottom={
                      index < chats.length - 1 ? "1px solid" : "none"
                    }
                    borderColor="gray.100"
                  >
                    <ChatListItem chat={chat} />
                  </Box>
                ))}
            </VStack>
          ) : loading ? (
            <Box p={4}>
              <ChatLoadingSkeleton />
            </Box>
          ) : (
            <Flex align="center" justify="center" h="full">
              <VStack spacing={4} textAlign="center">
                <Text fontSize="lg" color="gray.500" fontWeight="500">
                  No conversations yet
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Start a new conversation to begin chatting
                </Text>
              </VStack>
            </Flex>
          )}
        </Box>

        {/* Footer */}
        <Box
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <AppFooter />
        </Box>
      </Flex>
    </Box>
  );
};

export default ChatListPage;
